import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useToast,
  useColorModeValue,
  Switch,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useCreateVoucher } from 'queries/voucher.query'
import { useGetEvents } from 'queries/event.query'

export function CreateVoucherModal({ isOpen, onClose }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const toast = useToast()
  const createMutation = useCreateVoucher()
  const { data: eventsData } = useGetEvents(1, 100)
  const events = eventsData?.data || (Array.isArray(eventsData) ? eventsData : [])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      code: '',
      discount_type: 'percent',
      discount_value: 10,
      max_uses: 100,
      event_id: '',
      is_active: true,
    },
  })

  const discountType = watch('discount_type')

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        code: data.code.toUpperCase().trim(),
        event_id: data.event_id ? Number(data.event_id) : null,
        discount_value: Number(data.discount_value),
        max_uses: data.max_uses ? Number(data.max_uses) : null,
      }

      await createMutation.mutateAsync(payload)
      toast({
        title: 'Voucher created',
        description: `Promo code ${payload.code} is now ready`,
        status: 'success',
        duration: 3000,
      })
      reset()
      onClose()
    } catch (err) {
      toast({
        title: 'Failed to create voucher',
        description: err?.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 4000,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Create Promo / Voucher Code</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={errors.code}>
              <FormLabel>Promo Code</FormLabel>
              <Input
                color={inputColor}
                placeholder="e.g. BAMYAN2026 or NOWRUZ"
                style={{ textTransform: 'uppercase' }}
                {...register('code', { required: 'Code is required' })}
              />
            </FormControl>

            <HStack width="100%">
              <FormControl isRequired>
                <FormLabel>Discount Type</FormLabel>
                <Select color={inputColor} {...register('discount_type')}>
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (€ EUR)</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  Value ({discountType === 'percent' ? '%' : '€ EUR'})
                </FormLabel>
                <Input
                  type="number"
                  step="0.01"
                  color={inputColor}
                  {...register('discount_value', { required: true })}
                />
              </FormControl>
            </HStack>

            <HStack width="100%">
              <FormControl>
                <FormLabel>Max Uses</FormLabel>
                <Input
                  type="number"
                  color={inputColor}
                  placeholder="e.g. 100"
                  {...register('max_uses')}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Specific Event (Optional)</FormLabel>
                <Select color={inputColor} placeholder="All Events (Global)" {...register('event_id')}>
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb="0">Active Status</FormLabel>
              <Switch colorScheme="brand" defaultChecked {...register('is_active')} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="brand" type="submit" isLoading={isSubmitting || createMutation.isPending}>
            Create Voucher
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
